import { useSyncExternalStore } from "react";

export type Workspace = "design" | "execution" | "consulting";

export const WORKSPACES: Workspace[] = ["consulting", "design", "execution"];

/** Consulting services work as a design does, with no contractor in them. */
export function isDesignLike(mode: Workspace): boolean {
    return mode === "design" || mode === "consulting";
}

export function isWorkspace(value: unknown): value is Workspace {
    return WORKSPACES.includes(value as Workspace);
}

export const WORKSPACE_HEADER = "X-Pm-Workspace";

const STORAGE_KEY = "pm.workspace";

const listeners = new Set<() => void>();

function stored(): Workspace {
    try {
        const value = window.localStorage.getItem(STORAGE_KEY);
        return isWorkspace(value) ? value : "execution";
    } catch {
        return "execution";
    }
}

function persist(value: Workspace) {
    try {
        window.localStorage.setItem(STORAGE_KEY, value);
    } catch {
        return;
    }
}

let current: Workspace = stored();

export function getWorkspace(): Workspace {
    return current;
}

export function setWorkspace(next: Workspace) {
    if (next === current) {
        return;
    }
    current = next;
    persist(next);
    listeners.forEach((listener) => listener());
}

export function subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

export function useWorkspace(): Workspace {
    return useSyncExternalStore(subscribe, getWorkspace);
}
